import { useState } from "react";
import {
  ArrowLeft,
  AlertCircle,
  Plus,
  Trash2,
  Check,
  Pencil,
} from "lucide-react";

export const MARKET_OPTIONS = [
  { value: "total_bases", label: "Total Bases" },
  { value: "hits", label: "Hits" },
  { value: "home_runs", label: "Home Runs" },
  { value: "strikeouts", label: "Strikeouts" },
  { value: "runs", label: "Runs" },
  { value: "rbis", label: "RBIs" },
  { value: "walks", label: "Walks" },
  { value: "pitcher_outs", label: "Pitcher Outs" },
  { value: "moneyline", label: "Moneyline" },
  { value: "spread", label: "Spread" },
  { value: "game_total", label: "Game Total" },
  { value: "team_total", label: "Team Total" },
  { value: "unknown", label: "Unknown / Other" },
];

const newLeg = () => ({
  id: `manual-${Math.random().toString(36).slice(2, 10)}`,
  player_name: "",
  team: "",
  opponent: "",
  market: "unknown",
  original_market_text: "",
  side: null,
  line: null,
  odds: "",
  game_description: "",
  sport: "",
  league: "",
  confidence: "medium",
  needs_confirmation: true,
  _manual: true,
});

const FieldLabel = ({ children }) => (
  <label className="block text-[11px] uppercase tracking-wide text-slate-500 font-display">
    {children}
  </label>
);

const inputCls =
  "mt-1 w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500";

function LegCard({ leg, index, isEditing, onEdit, onDone, onDelete, onChange }) {
  const flag = leg.needs_confirmation;
  return (
    <div
      data-testid={`analyzer-leg-card-${index}`}
      className="rounded-2xl border p-4 md:p-5 mt-4 first:mt-0"
      style={{
        borderColor: flag ? "#f59e0b" : "#e2e8f0",
        background: flag ? "#fffbeb" : "#ffffff",
      }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="font-display text-sm text-slate-900">
          Leg {index + 1}
          <span
            className="ml-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full"
            style={{
              background:
                leg.confidence === "high"
                  ? "#dcfce7"
                  : leg.confidence === "medium"
                    ? "#fef3c7"
                    : "#fee2e2",
              color:
                leg.confidence === "high"
                  ? "#166534"
                  : leg.confidence === "medium"
                    ? "#92400e"
                    : "#991b1b",
            }}
            data-testid={`analyzer-leg-confidence-${index}`}
          >
            {leg.confidence}
          </span>
        </p>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <button
              type="button"
              onClick={onDone}
              data-testid={`analyzer-leg-done-${index}`}
              className="pa-btn pa-btn-primary text-xs"
            >
              <Check size={14} /> Done
            </button>
          ) : (
            <button
              type="button"
              onClick={onEdit}
              data-testid={`analyzer-leg-edit-${index}`}
              className="pa-btn pa-btn-ghost text-xs"
            >
              <Pencil size={14} /> Edit
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            data-testid={`analyzer-leg-delete-${index}`}
            className="pa-btn pa-btn-ghost text-xs"
            style={{ color: "#b91c1c" }}
          >
            <Trash2 size={14} /> Delete Leg
          </button>
        </div>
      </div>

      {flag && (
        <p
          data-testid={`analyzer-leg-flag-${index}`}
          className="mt-2 flex items-start gap-1.5 text-xs"
          style={{ color: "#92400e" }}
        >
          <AlertCircle size={14} className="mt-0.5" />
          Please confirm this information.
        </p>
      )}

      {isEditing ? (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <FieldLabel>Player / Team</FieldLabel>
            <input
              data-testid={`analyzer-leg-player-${index}`}
              className={inputCls}
              value={leg.player_name || ""}
              onChange={(e) => onChange({ player_name: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>Market</FieldLabel>
            <select
              data-testid={`analyzer-leg-market-${index}`}
              className={inputCls}
              value={leg.market || "unknown"}
              onChange={(e) => onChange({ market: e.target.value })}
            >
              {MARKET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel>Over / Under</FieldLabel>
            <select
              data-testid={`analyzer-leg-side-${index}`}
              className={inputCls}
              value={leg.side || ""}
              onChange={(e) =>
                onChange({ side: e.target.value || null })
              }
            >
              <option value="">—</option>
              <option value="over">Over</option>
              <option value="under">Under</option>
              <option value="home">Home</option>
              <option value="away">Away</option>
            </select>
          </div>
          <div>
            <FieldLabel>Line</FieldLabel>
            <input
              data-testid={`analyzer-leg-line-${index}`}
              className={inputCls}
              inputMode="decimal"
              value={leg.line ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "") return onChange({ line: null });
                const n = Number(v);
                onChange({ line: Number.isFinite(n) ? n : v });
              }}
            />
          </div>
          <div>
            <FieldLabel>Odds</FieldLabel>
            <input
              data-testid={`analyzer-leg-odds-${index}`}
              className={inputCls}
              value={leg.odds || ""}
              onChange={(e) => onChange({ odds: e.target.value })}
              placeholder="+120"
            />
          </div>
          <div>
            <FieldLabel>Team</FieldLabel>
            <input
              data-testid={`analyzer-leg-team-${index}`}
              className={inputCls}
              value={leg.team || ""}
              onChange={(e) => onChange({ team: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>Opponent</FieldLabel>
            <input
              data-testid={`analyzer-leg-opponent-${index}`}
              className={inputCls}
              value={leg.opponent || ""}
              onChange={(e) => onChange({ opponent: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>Sport</FieldLabel>
            <input
              data-testid={`analyzer-leg-sport-${index}`}
              className={inputCls}
              value={leg.sport || ""}
              onChange={(e) => onChange({ sport: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel>League</FieldLabel>
            <input
              data-testid={`analyzer-leg-league-${index}`}
              className={inputCls}
              value={leg.league || ""}
              onChange={(e) => onChange({ league: e.target.value })}
            />
          </div>
        </div>
      ) : (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
              Player / Team
            </dt>
            <dd className="text-slate-900" data-testid={`analyzer-leg-player-view-${index}`}>
              {leg.player_name || leg.team || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
              Market
            </dt>
            <dd className="text-slate-900">
              {leg.original_market_text ||
                MARKET_OPTIONS.find((o) => o.value === leg.market)?.label ||
                leg.market}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
              Side / Line
            </dt>
            <dd className="text-slate-900 font-mono-num">
              {leg.side ? `${leg.side} ` : ""}
              {leg.line ?? "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
              Odds
            </dt>
            <dd className="text-slate-900 font-mono-num">{leg.odds || "—"}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
              Game
            </dt>
            <dd className="text-slate-900">
              {leg.game_description ||
                (leg.team && leg.opponent
                  ? `${leg.team} vs ${leg.opponent}`
                  : "—")}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wide text-slate-500 font-display">
              Sport / League
            </dt>
            <dd className="text-slate-900">
              {[leg.sport, leg.league].filter(Boolean).join(" · ") || "—"}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

export function AnalyzerReview({
  slip,
  onSlipChange,
  onBack,
  onConfirm,
}) {
  const [editingId, setEditingId] = useState(null);
  const [sportsbook, setSportsbookLocal] = useState(slip.sportsbook || "Unknown");
  const [editingBook, setEditingBook] = useState(false);

  const legs = slip.legs || [];

  const patchLeg = (id, patch) => {
    onSlipChange({
      ...slip,
      legs: legs.map((l) =>
        l.id === id
          ? {
              ...l,
              ...patch,
              // Any manual change clears the confirmation flag for that leg.
              needs_confirmation: false,
            }
          : l,
      ),
    });
  };

  const removeLeg = (id) => {
    onSlipChange({
      ...slip,
      legs: legs.filter((l) => l.id !== id),
    });
  };

  const addLeg = () => {
    const leg = newLeg();
    onSlipChange({ ...slip, legs: [...legs, leg] });
    setEditingId(leg.id);
  };

  const commitSportsbook = () => {
    setEditingBook(false);
    onSlipChange({ ...slip, sportsbook: sportsbook || "Unknown" });
  };

  return (
    <section
      data-testid="analyzer-review"
      className="pa-card p-6 md:p-8 mt-6 md:mt-8"
    >
      <h2 className="font-display text-2xl md:text-3xl uppercase tracking-wide text-slate-900">
        We Found Your Bets
      </h2>
      <p className="mt-2 text-slate-600 text-sm md:text-base">
        Check everything below before PA analyzes your slip.
      </p>

      <div
        className="mt-4 flex items-center gap-2 flex-wrap"
        data-testid="analyzer-sportsbook-row"
      >
        <span className="font-display text-xs uppercase tracking-wide text-slate-500">
          Sportsbook:
        </span>
        {editingBook ? (
          <>
            <input
              data-testid="analyzer-sportsbook-input"
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
              value={sportsbook}
              onChange={(e) => setSportsbookLocal(e.target.value)}
            />
            <button
              type="button"
              onClick={commitSportsbook}
              data-testid="analyzer-sportsbook-save"
              className="pa-btn pa-btn-primary text-xs"
            >
              <Check size={14} /> Save
            </button>
          </>
        ) : (
          <>
            <span
              data-testid="analyzer-sportsbook-value"
              className="font-display text-sm text-slate-900"
              style={{
                color: slip.sportsbook === "Unknown" ? "#92400e" : undefined,
              }}
            >
              {slip.sportsbook === "Unknown"
                ? "Please confirm"
                : slip.sportsbook}
            </span>
            <button
              type="button"
              onClick={() => setEditingBook(true)}
              data-testid="analyzer-sportsbook-edit"
              className="pa-btn pa-btn-ghost text-xs"
            >
              <Pencil size={12} /> Edit
            </button>
          </>
        )}
      </div>

      <div className="mt-6" data-testid="analyzer-legs-list">
        {legs.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="analyzer-legs-empty">
            No legs yet. Use "Add Leg" to add one manually.
          </p>
        )}
        {legs.map((leg, i) => (
          <LegCard
            key={leg.id}
            leg={leg}
            index={i}
            isEditing={editingId === leg.id}
            onEdit={() => setEditingId(leg.id)}
            onDone={() => setEditingId(null)}
            onDelete={() => removeLeg(leg.id)}
            onChange={(patch) => patchLeg(leg.id, patch)}
          />
        ))}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={addLeg}
          data-testid="analyzer-add-leg-btn"
          className="pa-btn pa-btn-ghost"
        >
          <Plus size={16} /> Add Leg
        </button>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="analyzer-review-back-btn"
          className="pa-btn pa-btn-ghost"
        >
          <ArrowLeft size={16} /> Back to Image
        </button>
        <button
          type="button"
          onClick={onConfirm}
          data-testid="analyzer-review-confirm-btn"
          className="pa-btn pa-btn-primary"
          disabled={legs.length === 0}
          aria-disabled={legs.length === 0}
          style={legs.length === 0 ? { opacity: 0.4, cursor: "not-allowed" } : {}}
        >
          CONFIRM & CONTINUE
        </button>
      </div>
    </section>
  );
}
